import { 
  getAuth,
  User,
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  onAuthStateChanged,
  reload,
  GoogleAuthProvider,
  signInWithPopup,
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { doc, getDoc, setDoc, deleteDoc, addDoc, collection, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { signOutCleanly } from '../lib/authSession';
import { sanitizeInput, validateEmail, isCommonPassword } from '../utils/validation';
import { API_BASE_URL } from '../config/api';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const MAX_PASSWORD_AGE = 90 * 24 * 60 * 60 * 1000; // 90 jours

const LOGIN_FLOW_KEY = 'login_flow_active';
const TWO_FA_PENDING_KEY = '2fa_pending';

/** Bloque AuthRedirect pendant le flux login / 2FA en cours */
export function isLoginFlowBlockingRedirect(): boolean {
  return (
    sessionStorage.getItem(LOGIN_FLOW_KEY) === 'true' ||
    sessionStorage.getItem(TWO_FA_PENDING_KEY) === 'true' ||
    !!sessionStorage.getItem('2fa_pre_auth_token')
  );
}

function startLoginFlow(): void {
  sessionStorage.setItem(LOGIN_FLOW_KEY, 'true');
}

function clearLoginFlowFlags(): void {
  sessionStorage.removeItem(LOGIN_FLOW_KEY);
  sessionStorage.removeItem(TWO_FA_PENDING_KEY);
  sessionStorage.removeItem('2fa_pre_auth_token');
  sessionStorage.removeItem('2fa_pre_auth_expiry');
}
const MAX_LOGIN_ATTEMPTS = 5;
const LOGIN_ATTEMPT_WINDOW = 15 * 60 * 1000; // 15 minutes

interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  maxAge: number;
}

const PASSWORD_POLICY: PasswordPolicy = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
  maxAge: MAX_PASSWORD_AGE
};

const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // Sanitize input
  password = sanitizeInput(password);
  
  if (password.length < PASSWORD_POLICY.minLength) {
    errors.push(`Le mot de passe doit contenir au moins ${PASSWORD_POLICY.minLength} caractères`);
  }
  if (PASSWORD_POLICY.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Le mot de passe doit contenir au moins une lettre majuscule');
  }
  if (PASSWORD_POLICY.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Le mot de passe doit contenir au moins une lettre minuscule');
  }
  if (PASSWORD_POLICY.requireNumbers && !/\d/.test(password)) {
    errors.push('Le mot de passe doit contenir au moins un chiffre');
  }
  if (PASSWORD_POLICY.requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Le mot de passe doit contenir au moins un caractère spécial');
  }
  
  // Vérification des mots de passe courants
  if (isCommonPassword(password)) {
    errors.push('Ce mot de passe est trop commun. Veuillez choisir un mot de passe plus fort.');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Stockage des tentatives de connexion en mémoire
const loginAttempts = new Map<string, { count: number; timestamp: number }>();

const isLoginAllowed = (email: string): boolean => {
  const attempt = loginAttempts.get(email);
  if (!attempt) return true;

  const now = Date.now();
  if (now - attempt.timestamp > LOGIN_ATTEMPT_WINDOW) {
    loginAttempts.delete(email);
    return true;
  }

  return attempt.count < MAX_LOGIN_ATTEMPTS;
};

const recordLoginAttempt = (email: string, success: boolean) => {
  const now = Date.now();
  const attempt = loginAttempts.get(email);

  if (!attempt) {
    loginAttempts.set(email, { count: 1, timestamp: now });
    return;
  }

  if (now - attempt.timestamp > LOGIN_ATTEMPT_WINDOW) {
    loginAttempts.set(email, { count: 1, timestamp: now });
    return;
  }

  if (success) {
    loginAttempts.delete(email);
    return;
  }

  attempt.count++;
  attempt.timestamp = now;
};

async function retry<T>(fn: () => Promise<T>, retries = MAX_RETRIES): Promise<T> {
  try {
    return await fn();
  } catch (error: any) {
    if (retries > 0 && error.code === 'auth/network-request-failed') {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return retry(fn, retries - 1);
    }
    throw error;
  }
}

function handleAuthError(error: unknown): Error {
  // console.error supprimé
  
  if (error instanceof FirebaseError) {
    const customError = new Error(getErrorMessage(error.code));
    // Préserver le code d'erreur Firebase pour que les composants puissent l'utiliser
    (customError as any).code = error.code;
    return customError;
  }
  
  return new Error('Une erreur inattendue est survenue');
}

// Fonction pour obtenir le message d'erreur en fonction du code
function getErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case 'auth/network-request-failed':
      return 'Erreur de réseau. Veuillez vérifier votre connexion et réessayer.';
    case 'auth/email-already-in-use':
      return 'Cette adresse email est déjà utilisée';
    case 'auth/invalid-email':
      return 'Adresse email invalide';
    case 'auth/operation-not-allowed':
      return 'Opération non autorisée';
    case 'auth/weak-password':
      return 'Mot de passe trop faible';
    case 'auth/user-disabled':
      return 'Ce compte a été désactivé';
    case 'auth/user-not-found':
      return 'Aucun compte trouvé avec cette adresse email';
    case 'auth/wrong-password':
      return 'Mot de passe incorrect';
    case 'auth/requires-recent-login':
      return 'Veuillez vous reconnecter pour effectuer cette action';
    case 'auth/email-not-verified':
      return 'Veuillez vérifier votre adresse email avant de vous connecter';
    case 'auth/too-many-requests':
      return 'Trop de tentatives de connexion. Veuillez réessayer plus tard';
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return 'Mot de passe actuel incorrect';
    case 'auth/account-exists-with-different-credential':
      return 'Un compte existe déjà avec cette adresse email mais avec une méthode de connexion différente';
    case 'auth/no-such-provider':
      return 'Ce compte ne possède pas de mot de passe. Utilisez la connexion Google.';
    case 'permission-denied':
    case 'auth/insufficient-permission':
      return 'Erreur de permission. Veuillez vous reconnecter.';
    default:
      // console.error supprimé
      return 'Une erreur est survenue lors de l\'authentification';
  }
}

class AuthService {
  /**
   * Connecte un utilisateur avec son email et mot de passe.
   * Vérifie également si l'email est vérifié ou si l'utilisateur a un bypass de vérification.
   * @param email - L'email de l'utilisateur
   * @param password - Le mot de passe de l'utilisateur
   * @param rememberMe - Si true, la session sera persistante même après fermeture du navigateur
   * @returns Une promesse qui résout avec l'utilisateur connecté
   * @throws {Error} Si l'email n'est pas vérifié et qu'il n'y a pas de bypass
   */
  async login(email: string, password: string, rememberMe: boolean = false) {
    email = sanitizeInput(email);
    
    if (!validateEmail(email)) {
      throw new Error('Format d\'email invalide');
    }

    if (!isLoginAllowed(email)) {
      throw new Error('Trop de tentatives de connexion. Veuillez réessayer plus tard.');
    }

    startLoginFlow();

    try {
    const auth = getAuth();
    
    // Configurer la persistance en fonction de l'option "Se souvenir de moi"
    await setPersistence(auth, rememberMe ? 
      browserLocalPersistence : // Session persistante (même après fermeture du navigateur)
      browserSessionPersistence // Session temporaire (jusqu'à fermeture du navigateur)
    );
    
    // Stocker la préférence "Se souvenir de moi" dans localStorage
    localStorage.setItem('rememberMe', rememberMe.toString());
    
    // Connexion avec la persistance configurée
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

      // Vérifier si la 2FA est activée
      const twoFARequired = await this.check2FARequired(user.uid);
      
      if (twoFARequired) {
        // Capturer le token AVANT le signOut — il sera transmis à /api/verify-login
        // pour prouver l'identité de l'utilisateur sans que le backend accepte un userId
        // arbitraire dans le body.
        const preAuthToken = await user.getIdToken();
        sessionStorage.setItem('2fa_pre_auth_token', preAuthToken);
        sessionStorage.setItem('2fa_pre_auth_expiry', (Date.now() + 5 * 60 * 1000).toString());
        sessionStorage.setItem(TWO_FA_PENDING_KEY, 'true');

        // DÉCONNECTER l'utilisateur de Firebase Auth pour éviter que AuthContext le détecte.
        // Entre signInWithEmailAndPassword et ici, onAuthStateChanged a déjà fait monter
        // `user` dans AuthContext et les listeners users/{uid} sont actifs : un signOut
        // brut invaliderait le token sous leurs pieds (permission-denied). signOutCleanly
        // désabonne d'abord côté React, puis signOut.
        await signOutCleanly();
        
        // Retourner les données utilisateur pour le modal 2FA
        return { user, requires2FA: true };
      }

      // Finaliser la connexion uniquement si pas de 2FA
      await updateDoc(doc(db, 'users', user.uid), {
        isConnected: true,
        lastLoginAt: serverTimestamp()
      });

      clearLoginFlowFlags();
      return { user, requires2FA: false };
    } catch (error) {
      clearLoginFlowFlags();
      recordLoginAttempt(email, false);
      throw handleAuthError(error);
    }
  }

  /** Annule un flux 2FA en cours (ex. fermeture du modal) */
  clearPending2FALogin(): void {
    clearLoginFlowFlags();
  }

  /**
   * Résout avec l'uid si le serveur vient de créer le compte (201 + customToken),
   * `null` si l'email existait déjà (200 sans token : on ne fait que connecter).
   */
  async register(email: string, password: string, name: string, newsletterConsent: boolean = false): Promise<string | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/register`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          name,
          newsletterConsent
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Créer une erreur avec le code Firebase si disponible dans la réponse d'API
        const error = new Error(errorData.error || 'Erreur lors de l\'inscription');
        if (errorData.code && errorData.code.startsWith('auth/')) {
          (error as any).code = errorData.code;
        }
        throw error;
      }

      const data = await response.json();
      const auth = getAuth();

      if (data.customToken) {
        await signInWithCustomToken(auth, data.customToken);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }

      const currentUser = auth.currentUser;
      if (currentUser) {
        // Ne jamais bloquer l'inscription sur ce write : un canal Firestore pas encore
        // établi peut le faire attendre indéfiniment (bouton figé sur « Création… »).
        // Le serveur a déjà créé le compte, ce champ est purement informatif.
        const markConnected = updateDoc(doc(db, 'users', currentUser.uid), {
          isConnected: true,
          lastLoginAt: serverTimestamp(),
        }).catch(() => { /* ignore */ });
        await Promise.race([markConnected, new Promise((resolve) => setTimeout(resolve, 5000))]);
      }

      return data.customToken && currentUser ? currentUser.uid : null;
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  }

  async logout() {
    try {
      // Désabonne les listeners Firestore côté React avant d'invalider le token
      // (sinon permission-denied sur users/{uid} pendant la déconnexion).
      await signOutCleanly();
    } catch (error) {
      throw handleAuthError(error);
    }
  }

  async deleteAccount() {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) throw new Error('Aucun utilisateur connecté');

    try {
      await deleteDoc(doc(db, 'users', user.uid));
      
      await deleteUser(user);
    } catch (error) {
      throw handleAuthError(error);
    }
  }

  async sendPasswordReset(email: string): Promise<{ cooldownSeconds?: number }> {
    const response = await fetch(`${API_BASE_URL}/api/forgot-password`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.error || 'Erreur lors de l\'envoi de l\'email de réinitialisation');
    }

    return { cooldownSeconds: data.cooldownSeconds };
  }

  async resetPasswordWithToken(token: string, newPassword: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/reset-password-with-token`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, newPassword }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.error || 'Erreur lors de la réinitialisation du mot de passe');
    }
  }

  async resendVerificationEmail(): Promise<{ cooldownSeconds: number }> {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) throw new Error('Aucun utilisateur connecté');
    if (!user.email) throw new Error('Aucune adresse email associée à ce compte');

    try {
      const response = await fetch(`${API_BASE_URL}/api/resend-verification-email`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const error = new Error(data.error || 'Erreur lors du renvoi de l\'email de vérification') as Error & { secondsLeft?: number };
        if (typeof data.secondsLeft === 'number') {
          error.secondsLeft = data.secondsLeft;
        }
        throw error;
      }

      return { cooldownSeconds: data.cooldownSeconds ?? 60 };
    } catch (error) {
      throw error;
    }
  }

  async getVerificationResendCooldown(email: string): Promise<number> {
    const params = new URLSearchParams({ email });
    const response = await fetch(`${API_BASE_URL}/api/resend-verification-email/status?${params.toString()}`, {
      credentials: 'include',
    });

    if (!response.ok) return 0;

    const data = await response.json().catch(() => ({}));
    return typeof data.secondsLeft === 'number' ? data.secondsLeft : 0;
  }

  async reloadUser() {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) throw new Error('Aucun utilisateur connecté');

    try {
      await reload(user);
    } catch (error) {
      throw handleAuthError(error);
    }
  }

  getCurrentUser(): User | null {
    const auth = getAuth();
    return auth.currentUser;
  }

  async logAuthAttempt(email: string, success: boolean) {
    try {
      await addDoc(collection(db, 'authLogs'), {
        email,
        success,
        timestamp: new Date().toISOString(),
        ip: window.sessionStorage.getItem('userIP') || 'unknown',
        userAgent: navigator.userAgent,
        geoLocation: window.sessionStorage.getItem('userLocation') || 'unknown'
      });
    } catch (error) {
      // console.error supprimé
    }
  }

  async getLastPasswordChange(userId: string): Promise<number | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists() && userDoc.data().lastPasswordChange) {
        return new Date(userDoc.data().lastPasswordChange).getTime();
      }
      return null;
    } catch (error) {
      // console.error supprimé
      return null;
    }
  }

  /**
   * Finaliser la connexion après validation 2FA réussie
   * Note: L'utilisateur a été déconnecté de Firebase Auth pendant la 2FA
   * Cette méthode reconnecte l'utilisateur sans re-vérifier la 2FA
   */
  async finalize2FALogin(email: string, password: string, rememberMe: boolean): Promise<User> {
    try {
      // console.log supprimé
      
      const auth = getAuth();
      
      // Configurer la persistance en fonction de l'option "Se souvenir de moi"
      await setPersistence(auth, rememberMe ? 
        browserLocalPersistence : // Session persistante
        browserSessionPersistence // Session temporaire
      );
      
      // Stocker la préférence "Se souvenir de moi" dans localStorage
      localStorage.setItem('rememberMe', rememberMe.toString());
      
      // Marquer temporairement que la 2FA a été validée pour cette session
      sessionStorage.setItem('2fa_validated_temp', 'true');
      
      // Reconnexion avec la persistance configurée
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Finaliser la connexion dans Firestore
      await updateDoc(doc(db, 'users', user.uid), {
        isConnected: true,
        lastLoginAt: serverTimestamp()
      });
      
      // Nettoyer le flag temporaire après un délai
      setTimeout(() => {
        sessionStorage.removeItem('2fa_validated_temp');
      }, 5000);

      clearLoginFlowFlags();
      
      // console.log supprimé
      return user;
    } catch (error) {
      // console.error supprimé
      throw handleAuthError(error);
    }
  }

  /**
   * Vérifier si la 2FA est requise pour un utilisateur
   */
  async check2FARequired(userId: string): Promise<boolean> {
    try {
      // console.log supprimé
      
      // Si la 2FA a été validée temporairement dans cette session, ne pas la redemander
      if (sessionStorage.getItem('2fa_validated_temp') === 'true') {
        // console.log supprimé
        return false;
      }
      
      // Import dynamique pour éviter les dépendances circulaires
      const { TwoFactorAPIService } = await import('./twoFactorAPIService');
      const result = await TwoFactorAPIService.check2FARequired(userId);
      
      // console.log supprimé
      // console.log supprimé
      
      return result.required === true;
    } catch (error) {
      // En cas d'erreur réseau, assumons pas de 2FA pour éviter blocage
      // console.error supprimé
      
      // TEMPORAIRE : Retourner true si l'utilisateur a l'air d'avoir la 2FA activée
      // On va checker directement Firestore en fallback
      // console.log supprimé
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        const userData = userDoc.data();
        const has2FA = userData?.['2fa_enabled'] === true;
        // console.log supprimé
        return has2FA;
      } catch (firestoreError) {
        // console.error supprimé
        return false;
      }
    }
  }

  async completeRegistration(token: string, email: string, password: string, name: string): Promise<void> {
    try {
      // console.log supprimé
      const response = await fetch(`https://api.focuspips.com:4173/api/check-token/${token}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Token invalide ou expiré');
      }

      if (data.userExists) {
        throw new Error('Un compte existe déjà avec cet email');
      }

      if (data.email !== email) {
        throw new Error('L\'email ne correspond pas au token');
      }

      // console.log supprimé
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.isValid) {
        throw new Error(passwordValidation.errors.join('\n'));
      }

      // console.log supprimé
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // console.log supprimé
      await updateProfile(user, {
        displayName: name
      });

      const isPremium = data.isPremium || false;
      // console.log supprimé
      await setDoc(doc(db, 'users', user.uid), {
        name,
        email,
        createdAt: serverTimestamp(),
        lastPasswordChange: serverTimestamp(),
        emailVerified: isPremium, // Email déjà vérifié si l'utilisateur est premium
        isPremium: isPremium,
        customerId: data.customerId || null,
        subscriptionId: data.subscriptionId || null,
        subscriptionStatus: data.subscriptionStatus || null,
        subscriptionEndDate: data.subscriptionEndDate || null
      });

      // Envoyer l'email de vérification seulement si l'utilisateur n'est pas premium
      if (!isPremium) {
        // console.log supprimé
        try {
          await sendEmailVerification(user);
          // console.log supprimé
        } catch (verificationError) {
          // console.error supprimé
          // On ne throw pas l'erreur ici car le compte est déjà créé
        }
      }

      // console.log supprimé
      const completeResponse = await fetch(`${API_BASE_URL}/api/complete-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, email })
      });

      if (!completeResponse.ok) {
        const errorData = await completeResponse.json();
        throw new Error(errorData.error || 'Erreur lors de la finalisation de l\'inscription');
      }

      // console.log supprimé
    } catch (error: any) {
      // console.error supprimé
      if (error instanceof FirebaseError) {
        // Gérer les erreurs Firebase spécifiques
        switch (error.code) {
          case 'auth/email-already-in-use':
            throw new Error('Un compte existe déjà avec cet email');
          case 'auth/invalid-email':
            throw new Error('L\'adresse email n\'est pas valide');
          case 'auth/operation-not-allowed':
            throw new Error('L\'inscription par email n\'est pas activée');
          case 'auth/weak-password':
            throw new Error('Le mot de passe est trop faible');
          case 'permission-denied':
          case 'auth/insufficient-permission':
            throw new Error('Erreur de permission. Veuillez vous reconnecter.');
          default:
            throw new Error('Une erreur est survenue lors de l\'inscription');
        }
      }
      throw error;
    }
  }

  async changePassword(currentPassword: string, newPassword: string) {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user || !user.email) {
      throw new Error('Aucun utilisateur connecté');
    }

    // Vérifier que le user a bien un provider email/password
    const hasPasswordProvider = user.providerData.some(p => p.providerId === 'password');
    if (!hasPasswordProvider) {
      throw new Error('Ce compte utilise la connexion Google et ne possède pas de mot de passe. Vous ne pouvez pas modifier votre mot de passe ici.');
    }

    // Valider le nouveau mot de passe
    const { isValid, errors } = validatePassword(newPassword);
    if (!isValid) {
      throw new Error(errors.join('. '));
    }

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      
      // Mettre à jour la date de changement du mot de passe
      await setDoc(doc(db, 'users', user.uid), {
        lastPasswordChange: new Date().toISOString()
      }, { merge: true });

      // Logger le changement de mot de passe
      await this.logAuthAttempt(user.email, true);
    } catch (error) {
      await this.logAuthAttempt(user.email, false);
      throw handleAuthError(error);
    }
  }

  async loginWithGoogle() {
    try {
      // console.log supprimé
      const auth = getAuth();
      const provider = new GoogleAuthProvider();
      
      // Configuration simple et efficace selon le tutoriel
      provider.addScope('profile');
      provider.addScope('email');
      
      // console.log supprimé
      // Authentification avec popup (méthode recommandée)
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // console.log supprimé
      
      // Appeler l'API backend pour créer/mettre à jour l'utilisateur
      // console.log supprimé
      try {
        const idToken = await user.getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/register-google`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            emailVerified: user.emailVerified
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          // console.warn supprimé
          // On continue même si l'API échoue, l'AuthContext va essayer de créer l'utilisateur
        } else {
          const data = await response.json();
          // console.log supprimé
        }
      } catch (apiError) {
        // console.warn supprimé
        // On continue même si l'API échoue
      }
      
      // console.log supprimé
      return user;
    } catch (error: any) {
      // console.error supprimé
      
      // Ne pas traiter les warnings CORS comme des erreurs fatales
      if (error.message?.includes('Cross-Origin-Opener-Policy')) {
        // console.log supprimé
        // Vérifier si l'utilisateur est quand même connecté
        const auth = getAuth();
        if (auth.currentUser) {
          // console.log supprimé
          return auth.currentUser;
        }
      }
      
      // Gestion des erreurs spécifiques selon le tutoriel
      switch (error.code) {
        case 'auth/popup-blocked':
          throw new Error('Les popups sont bloqués par votre navigateur. Veuillez autoriser les popups pour ce site et réessayer.');
        case 'auth/popup-closed-by-user':
          throw new Error('Connexion annulée. Veuillez réessayer.');
        case 'auth/cancelled-popup-request':
          throw new Error('Une autre demande de connexion était en cours. Veuillez réessayer.');
        case 'auth/account-exists-with-different-credential':
          throw new Error('Un compte existe déjà avec cette adresse email mais avec une méthode de connexion différente.');
        case 'auth/operation-not-allowed':
          throw new Error('L\'authentification Google n\'est pas activée. Veuillez contacter le support.');
        case 'auth/unauthorized-domain':
          throw new Error('Ce domaine n\'est pas autorisé pour l\'authentification Google. Veuillez contacter le support.');
        case 'auth/network-request-failed':
          throw new Error('Erreur de réseau. Vérifiez votre connexion internet et réessayez.');
        default:
          throw new Error(`Erreur d'authentification Google: ${error.message}`);
      }
    }
  }
}

export const authService = new AuthService();
